import React, { useContext, useEffect, useState, useCallback } from "react";
import Context from "../../../Context";
import { apiFetch } from "../../../utils/api";
import { 
  Bell, 
  Mail, 
  Clock, 
  Calendar,
  ChevronRight,
  Check,
  X,
  Send,
  FileText,
  TrendingUp,
  AlertCircle,
  Trash2,
  Eye,
  RefreshCw,
  Settings,
  MessageSquare,
  FileCheck
} from "lucide-react";
import { auth, db } from "../../../firebase/config";
import { doc, getDoc, setDoc, collection, query, orderBy, limit, getDocs, deleteDoc } from "firebase/firestore";
import styles from "./Reports.module.scss";

interface NotificationSettings {
  enabled: boolean;
  email: string;
  dailyEnabled: boolean;
  dailyTime: string; // HH:MM format
  weeklyEnabled: boolean;
  weeklyDay: number; // 0-6 (Sunday-Saturday)
  weeklyTime: string;
  monthlyEnabled: boolean;
  monthlyDay: number; // 1-28
  monthlyTime: string;
  timezone: string;
}

interface Transaction {
  name: string;
  amount: number;
  date: string;
  category: string;
  pending: boolean;
}

interface Report {
  id: string;
  type: 'daily' | 'weekly' | 'monthly';
  date: string;
  dateRange: { start: string; end: string };
  totalSpent: number;
  topCategories?: { category: string; amount: number; percentage: number }[];
  transactions?: Transaction[];
  transactionCount: number;
  aiSummary: string;
  upcomingBills?: { name: string; amount: number; dueDate: string }[];
  paidBills?: { name: string; amount: number; paidDate: string }[];
  budgetStatus?: { category: string; spent: number; limit: number; percentage: number }[];
  sentAt?: string;
  sentVia?: 'email' | 'sms';
  createdAt: string;
}

const DEFAULT_SETTINGS: NotificationSettings = {
  enabled: false,
  email: '',
  dailyEnabled: true,
  dailyTime: '08:00',
  weeklyEnabled: true,
  weeklyDay: 0, // Sunday
  weeklyTime: '09:00',
  monthlyEnabled: true,
  monthlyDay: 1,
  monthlyTime: '09:00',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
};

const Reports: React.FC = () => {
  const { linkSuccess } = useContext(Context);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [settings, setSettings] = useState<NotificationSettings>(DEFAULT_SETTINGS);
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [activeTab, setActiveTab] = useState<'settings' | 'history'>('settings');
  const [reportType, setReportType] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [lastGeneratedReport, setLastGeneratedReport] = useState<Report | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showMessage = useCallback((type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      if (!auth.currentUser) return;
      
      const settingsDoc = await getDoc(doc(db, "users", auth.currentUser.uid, "settings", "notifications"));
      if (settingsDoc.exists()) {
        setSettings({ ...DEFAULT_SETTINGS, ...settingsDoc.data() as NotificationSettings });
      } else {
        // Set default email from user profile
        const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setSettings(prev => ({
            ...prev,
            email: userData.email || auth.currentUser?.email || ''
          }));
        }
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      showMessage('error', 'Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  const loadReports = useCallback(async () => {
    try {
      if (!auth.currentUser) return;
      
      const reportsRef = collection(db, "users", auth.currentUser.uid, "reports");
      const q = query(reportsRef, orderBy("createdAt", "desc"), limit(50));
      const snapshot = await getDocs(q);
      
      const loadedReports: Report[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        // Convert Firestore Timestamp to ISO string if needed
        if (data.createdAt?.toDate) {
          data.createdAt = data.createdAt.toDate().toISOString();
        }
        if (data.date?.toDate) {
          data.date = data.date.toDate().toISOString();
        }
        loadedReports.push({ id: doc.id, ...data } as Report);
      });
      
      console.log('Loaded reports:', loadedReports.length);
      setReports(loadedReports);
    } catch (error) {
      console.error("Error loading reports:", error);
      showMessage('error', 'Failed to load reports');
    }
  }, [showMessage]);

  // Load settings and reports
  useEffect(() => {
    if (linkSuccess) {
      loadSettings();
      loadReports();
    } else {
      setLoading(false);
    }
  }, [linkSuccess, loadSettings, loadReports]);

  const saveSettings = async () => {
    try {
      setSaving(true);
      if (!auth.currentUser) throw new Error("Not authenticated");
      
      // Save to Firestore
      await setDoc(
        doc(db, "users", auth.currentUser.uid, "settings", "notifications"),
        settings,
        { merge: true }
      );
      
      // Update server-side schedule
      try {
        const scheduleResponse = await apiFetch('/api/reports/schedule', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: auth.currentUser.uid,
            settings: settings
          })
        });
        
        const scheduleData = await scheduleResponse.json();
        if (scheduleData.success) {
          console.log('Schedule updated:', scheduleData.message);
        }
      } catch (scheduleError) {
        console.error('Failed to update schedule on server:', scheduleError);
        // Don't fail the whole save, settings are still saved to Firestore
      }
      
      showMessage('success', 'Settings saved successfully!');
    } catch (error) {
      console.error("Error saving settings:", error);
      showMessage('error', 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const sendReport = async () => {
    try {
      setSendingTest(true);
      if (!auth.currentUser) throw new Error("Not authenticated");
      
      console.log('Sending report:', reportType, 'to:', settings.email);
      
      const response = await apiFetch('/api/generate-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: auth.currentUser.uid,
          type: reportType,
          sendEmail: true,
          email: settings.email
        })
      });
      
      const data = await response.json();
      console.log('Server response:', data);
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send report');
      }
      
      if (data.report?.emailError) {
        showMessage('error', `Report generated but email failed: ${data.report.emailError}`);
      } else if (data.report?.sentAt) {
        showMessage('success', `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report sent to ${settings.email}!`);
      } else {
        showMessage('error', 'Report generated but email status unknown. Check spam folder.');
      }
      
      await loadReports();
    } catch (error) {
      console.error("Error sending report:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showMessage('error', `Failed to send report: ${errorMessage}`);
    } finally {
      setSendingTest(false);
    }
  };

  const generateReport = async (type: 'daily' | 'weekly' | 'monthly') => {
    try {
      setSaving(true);
      if (!auth.currentUser) throw new Error("Not authenticated");
      
      const response = await apiFetch('/api/generate-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: auth.currentUser.uid,
          type,
          sendEmail: false,
          email: settings.email
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate report');
      }
      
      const data = await response.json();
      setLastGeneratedReport(data.report);
      showMessage('success', `${type.charAt(0).toUpperCase() + type.slice(1)} report generated successfully!`);
      
      // Reload reports after a small delay to ensure Firestore has saved
      setTimeout(async () => {
        await loadReports();
      }, 1000);
    } catch (error) {
      console.error("Error generating report:", error);
      showMessage('error', 'Failed to generate report');
    } finally {
      setSaving(false);
    }
  };

  const deleteReport = async (reportId: string) => {
    try {
      if (!auth.currentUser) return;
      
      await deleteDoc(doc(db, "users", auth.currentUser.uid, "reports", reportId));
      setReports(prev => prev.filter(r => r.id !== reportId));
      showMessage('success', 'Report deleted');
    } catch (error) {
      console.error("Error deleting report:", error);
      showMessage('error', 'Failed to delete report');
    }
  };


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  if (loading) {
    return (
      <div className={styles.reports}>
        <div className={styles.loadingState}>
          <RefreshCw className={styles.spinner} size={32} />
          <p>Loading notification settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.reports}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerIcon}>
            <Bell size={28} />
          </div>
          <div>
            <h1>Reports & Notifications</h1>
            <p>Manage your spending reports and notification preferences</p>
          </div>
        </div>
      </div>

      {/* Message Toast */}
      {message && (
        <div className={`${styles.toast} ${styles[message.type]}`}>
          {message.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)}><X size={16} /></button>
        </div>
      )}

      {/* Tabs */}
      <div className={styles.tabs}>
        <button 
          className={`${styles.tab} ${activeTab === 'settings' ? styles.active : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <Settings size={18} />
          Settings
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'history' ? styles.active : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <FileText size={18} />
          Report History
        </button>
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className={styles.settingsContainer}>
          {/* Email Notifications & Address Side by Side */}
          <div className={styles.settingsCard}>
            <div className={styles.sideBySideContainer}>
              {/* Email Address - Left Side */}
              <div className={styles.leftSection}>
                <div className={styles.emailLabel}>
                  <Mail size={18} />
                  <span>Email Address</span>
                </div>
                <div className={styles.inputGroup}>
                  <input
                    type="email"
                    value={settings.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSettings(prev => ({ ...DEFAULT_SETTINGS, ...prev, email: value }));
                    }}
                    placeholder="your@email.com"
                    className={styles.input}
                  />
                  <button 
                    className={styles.testBtn}
                    onClick={sendReport}
                    disabled={sendingTest || !settings.email}
                  >
                    {sendingTest ? <RefreshCw size={16} className={styles.spinning} /> : <Send size={16} />}
                    Send Report
                  </button>
                </div>
              </div>

              {/* Separator */}
              <div className={styles.separator}></div>

              {/* Email Notifications - Right Side */}
              <div className={styles.rightSection}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitle}>
                    <Bell size={20} />
                    <span>Email Notifications</span>
                  </div>
                  <label className={styles.toggle}>
                    <input 
                      type="checkbox" 
                      checked={settings.enabled}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSettings(prev => ({ ...DEFAULT_SETTINGS, ...prev, enabled: checked }));
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
                <p className={styles.cardDescription}>
                  Receive automated spending reports via email at your preferred schedule.
                </p>
              </div>
            </div>
          </div>

          {/* Report Type Tabs */}
          <div className={styles.reportTypeControls}>
            <button
              className={`${styles.reportTypeBtn} ${reportType === 'daily' ? styles.active : ''}`}
              onClick={() => setReportType('daily')}
            >
              <Calendar size={16} />
              <span>Daily Reports</span>
            </button>
            <button
              className={`${styles.reportTypeBtn} ${reportType === 'weekly' ? styles.active : ''}`}
              onClick={() => setReportType('weekly')}
            >
              <TrendingUp size={16} />
              <span>Weekly Reports</span>
            </button>
            <button
              className={`${styles.reportTypeBtn} ${reportType === 'monthly' ? styles.active : ''}`}
              onClick={() => setReportType('monthly')}
            >
              <FileText size={16} />
              <span>Monthly Reports</span>
            </button>
          </div>

          {/* Daily Reports */}
          {reportType === 'daily' && (
          <div className={`${styles.settingsCard} ${!settings.enabled ? styles.disabled : ''}`}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <Calendar size={20} />
                <span>Daily Reports</span>
              </div>
              <label className={styles.toggle}>
                <input 
                  type="checkbox" 
                  checked={settings.dailyEnabled}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSettings(prev => ({ ...DEFAULT_SETTINGS, ...prev, dailyEnabled: checked }));
                  }}
                  disabled={!settings.enabled}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
            <p className={styles.cardDescription}>
              Receive a summary of yesterday's spending, upcoming bills, and AI-generated insights every morning.
            </p>
            <div className={styles.scheduleRow}>
              <Clock size={16} />
              <span>Send at</span>
              <input
                type="time"
                value={settings.dailyTime}
                onChange={(e) => {
                  const value = e.target.value;
                  setSettings(prev => ({ ...DEFAULT_SETTINGS, ...prev, dailyTime: value }));
                }}
                className={styles.timeInput}
                disabled={!settings.enabled || !settings.dailyEnabled}
              />
            </div>
            <div className={styles.reportPreview}>
              <h4>What's included:</h4>
              {lastGeneratedReport && lastGeneratedReport.type === 'daily' && reportType === 'daily' ? (
                <div 
                  className={styles.viewReportLink}
                  onClick={() => setSelectedReport(lastGeneratedReport)}
                >
                  <FileCheck size={18} />
                  <span>Report generated! Click here to view</span>
                </div>
              ) : (
                <ul>
                  <li>Total spending yesterday</li>
                  <li>Top categories & transactions</li>
                  <li>Upcoming bills this week</li>
                  <li>Bills paid yesterday</li>
                  <li>AI-generated spending insights</li>
                </ul>
              )}
            </div>
            <button 
              className={styles.generateBtn}
              onClick={() => generateReport('daily')}
              disabled={saving || !settings.email}
            >
              <FileText size={16} />
              Generate Daily Report Now
            </button>
          </div>
          )}

          {/* Weekly Reports */}
          {reportType === 'weekly' && (
          <div className={`${styles.settingsCard} ${!settings.enabled ? styles.disabled : ''}`}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <TrendingUp size={20} />
                <span>Weekly Reports</span>
              </div>
              <label className={styles.toggle}>
                <input 
                  type="checkbox" 
                  checked={settings.weeklyEnabled}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSettings(prev => ({ ...DEFAULT_SETTINGS, ...prev, weeklyEnabled: checked }));
                  }}
                  disabled={!settings.enabled}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
            <p className={styles.cardDescription}>
              Get a comprehensive weekly summary compiled from your daily reports.
            </p>
            <div className={styles.scheduleRow}>
              <Calendar size={16} />
              <span>Send on</span>
              <select
                value={settings.weeklyDay}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  setSettings(prev => ({ ...DEFAULT_SETTINGS, ...prev, weeklyDay: value }));
                }}
                className={styles.select}
                disabled={!settings.enabled || !settings.weeklyEnabled}
              >
                {weekDays.map((day, i) => (
                  <option key={i} value={i}>{day}</option>
                ))}
              </select>
              <span>at</span>
              <input
                type="time"
                value={settings.weeklyTime}
                onChange={(e) => {
                  const value = e.target.value;
                  setSettings(prev => ({ ...DEFAULT_SETTINGS, ...prev, weeklyTime: value }));
                }}
                className={styles.timeInput}
                disabled={!settings.enabled || !settings.weeklyEnabled}
              />
            </div>
            <div className={styles.reportPreview}>
              <h4>What's included:</h4>
              {lastGeneratedReport && lastGeneratedReport.type === 'weekly' && reportType === 'weekly' ? (
                <div 
                  className={styles.viewReportLink}
                  onClick={() => setSelectedReport(lastGeneratedReport)}
                >
                  <FileCheck size={18} />
                  <span>Report generated! Click here to view</span>
                </div>
              ) : (
                <ul>
                  <li>Week's total spending & comparison</li>
                  <li>Category breakdown</li>
                  <li>Daily spending trends</li>
                  <li>All transactions summary</li>
                  <li>AI analysis of spending patterns</li>
                </ul>
              )}
            </div>
            <button 
              className={styles.generateBtn}
              onClick={() => generateReport('weekly')}
              disabled={saving || !settings.email}
            >
              <FileText size={16} />
              Generate Weekly Report Now
            </button>
          </div>
          )}

          {/* Monthly Reports */}
          {reportType === 'monthly' && (
          <div className={`${styles.settingsCard} ${!settings.enabled ? styles.disabled : ''}`}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <FileText size={20} />
                <span>Monthly Reports</span>
              </div>
              <label className={styles.toggle}>
                <input 
                  type="checkbox" 
                  checked={settings.monthlyEnabled}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSettings(prev => ({ ...DEFAULT_SETTINGS, ...prev, monthlyEnabled: checked }));
                  }}
                  disabled={!settings.enabled}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
            <p className={styles.cardDescription}>
              Comprehensive monthly analysis including budget tracking and financial health score.
            </p>
            <div className={styles.scheduleRow}>
              <Calendar size={16} />
              <span>Send on day</span>
              <select
                value={settings.monthlyDay}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  setSettings(prev => ({ ...DEFAULT_SETTINGS, ...prev, monthlyDay: value }));
                }}
                className={styles.select}
                disabled={!settings.enabled || !settings.monthlyEnabled}
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
              <span>at</span>
              <input
                type="time"
                value={settings.monthlyTime}
                onChange={(e) => {
                  const value = e.target.value;
                  setSettings(prev => ({ ...DEFAULT_SETTINGS, ...prev, monthlyTime: value }));
                }}
                className={styles.timeInput}
                disabled={!settings.enabled || !settings.monthlyEnabled}
              />
            </div>
            <div className={styles.reportPreview}>
              <h4>What's included:</h4>
              {lastGeneratedReport && lastGeneratedReport.type === 'monthly' && reportType === 'monthly' ? (
                <div 
                  className={styles.viewReportLink}
                  onClick={() => setSelectedReport(lastGeneratedReport)}
                >
                  <FileCheck size={18} />
                  <span>Report generated! Click here to view</span>
                </div>
              ) : (
                <ul>
                  <li>Month's total spending</li>
                  <li>Budget vs actual comparison</li>
                  <li>Over-budget categories</li>
                  <li>Weekly spending breakdown</li>
                  <li>Year-to-date summary</li>
                  <li>AI recommendations</li>
                </ul>
              )}
            </div>
            <button 
              className={styles.generateBtn}
              onClick={() => generateReport('monthly')}
              disabled={saving || !settings.email}
            >
              <FileText size={16} />
              Generate Monthly Report Now
            </button>
          </div>
          )}

          {/* Save Button */}
          <button 
            className={styles.saveBtn}
            onClick={saveSettings}
            disabled={saving}
          >
            {saving ? <RefreshCw size={18} className={styles.spinning} /> : <Check size={18} />}
            Save Settings
          </button>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className={styles.historyContainer}>
          {reports.length === 0 ? (
            <div className={styles.emptyState}>
              <FileText size={48} />
              <h3>No Reports Yet</h3>
              <p>Your generated reports will appear here.</p>
              <button 
                className={styles.generateBtn}
                onClick={() => generateReport('daily')}
                disabled={saving || !settings.email}
              >
                Generate Your First Report
              </button>
            </div>
          ) : (
            <div className={styles.reportsList}>
              {reports.map(report => (
                <div 
                  key={report.id} 
                  className={styles.reportItem}
                  onClick={() => setSelectedReport(report)}
                >
                  <div className={styles.reportIcon}>
                    {report.type === 'daily' && <Calendar size={20} />}
                    {report.type === 'weekly' && <TrendingUp size={20} />}
                    {report.type === 'monthly' && <FileText size={20} />}
                  </div>
                  <div className={styles.reportInfo}>
                    <h4>{report.type.charAt(0).toUpperCase() + report.type.slice(1)} Report</h4>
                    <span className={styles.reportDate}>{formatDate(report.date)}</span>
                    {report.dateRange && (
                      <span className={styles.reportRange}>
                        {formatDate(report.dateRange.start)} - {formatDate(report.dateRange.end)}
                      </span>
                    )}
                  </div>
                  <div className={styles.reportStats}>
                    <span className={styles.reportTotal}>{formatCurrency(report.totalSpent)}</span>
                    <span className={styles.reportTxns}>{report.transactionCount} transactions</span>
                  </div>
                  <div className={styles.reportActions}>
                    <button 
                      className={styles.viewBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedReport(report);
                      }}
                    >
                      <Eye size={16} />
                    </button>
                    <button 
                      className={styles.deleteBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteReport(report.id);
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <ChevronRight size={18} className={styles.chevron} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Report Detail Modal */}
      {selectedReport && (
        <>
          <div className={styles.modalBackdrop} onClick={() => setSelectedReport(null)} />
          <div className={styles.reportModal}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                <div className={styles.modalIcon}>
                  {selectedReport.type === 'daily' && <Calendar size={24} />}
                  {selectedReport.type === 'weekly' && <TrendingUp size={24} />}
                  {selectedReport.type === 'monthly' && <FileText size={24} />}
                </div>
                <div>
                  <h2>{selectedReport.type.charAt(0).toUpperCase() + selectedReport.type.slice(1)} Report</h2>
                  <span className={styles.modalDate}>{formatDate(selectedReport.date)}</span>
                </div>
              </div>
              <button className={styles.closeBtn} onClick={() => setSelectedReport(null)}>
                <X size={24} />
              </button>
            </div>

            <div className={styles.modalContent}>
              {/* Summary */}
              <div className={styles.modalSection}>
                <h3>Summary</h3>
                <div className={styles.summaryGrid}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Total Spent</span>
                    <span className={styles.summaryValue}>{formatCurrency(selectedReport.totalSpent)}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Transactions</span>
                    <span className={styles.summaryValue}>{selectedReport.transactionCount}</span>
                  </div>
                  {selectedReport.dateRange && (
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabel}>Period</span>
                      <span className={styles.summaryValue}>
                        {selectedReport.type === 'daily' 
                          ? formatDate(selectedReport.dateRange.start)
                          : `${formatDate(selectedReport.dateRange.start)} - ${formatDate(selectedReport.dateRange.end)}`
                        }
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Transactions List (Daily Reports) */}
              {selectedReport.type === 'daily' && selectedReport.transactions && selectedReport.transactions.length > 0 && (
                <div className={styles.modalSection}>
                  <h3>📋 Transactions</h3>
                  <div className={styles.transactionsList}>
                    {selectedReport.transactions.map((tx, i) => (
                      <div key={i} className={styles.transactionItem}>
                        <div className={styles.transactionInfo}>
                          <span className={styles.transactionName}>{tx.name}</span>
                          <span className={styles.transactionCategory}>{tx.category.replace(/_/g, ' ')}</span>
                        </div>
                        <span className={styles.transactionAmount}>{formatCurrency(tx.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Categories (Weekly/Monthly Reports) */}
              {selectedReport.type !== 'daily' && selectedReport.topCategories && selectedReport.topCategories.length > 0 && (
                <div className={styles.modalSection}>
                  <h3>📊 Spending by Category</h3>
                  <div className={styles.categoriesList}>
                    {selectedReport.topCategories.map((cat, i) => (
                      <div key={i} className={styles.categoryItem}>
                        <span className={styles.categoryName}>{cat.category.replace(/_/g, ' ')}</span>
                        <div className={styles.categoryBar}>
                          <div 
                            className={styles.categoryFill} 
                            style={{ width: `${cat.percentage}%` }}
                          />
                        </div>
                        <span className={styles.categoryAmount}>{formatCurrency(cat.amount)}</span>
                        <span className={styles.categoryPercent}>{cat.percentage.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Budget Status (Monthly) */}
              {selectedReport.budgetStatus && selectedReport.budgetStatus.length > 0 && (
                <div className={styles.modalSection}>
                  <h3>Budget Status</h3>
                  <div className={styles.budgetList}>
                    {selectedReport.budgetStatus.map((item, i) => (
                      <div key={i} className={`${styles.budgetItem} ${item.percentage > 100 ? styles.overBudget : ''}`}>
                        <div className={styles.budgetHeader}>
                          <span className={styles.budgetCategory}>{item.category.replace(/_/g, ' ')}</span>
                          <span className={styles.budgetPercent}>{item.percentage.toFixed(0)}%</span>
                        </div>
                        <div className={styles.budgetBar}>
                          <div 
                            className={styles.budgetFill} 
                            style={{ width: `${Math.min(item.percentage, 100)}%` }}
                          />
                        </div>
                        <div className={styles.budgetDetails}>
                          <span>{formatCurrency(item.spent)} / {formatCurrency(item.limit)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Summary */}
              {selectedReport.aiSummary && (
                <div className={styles.modalSection}>
                  <h3>
                    <MessageSquare size={18} />
                    AI Insights
                  </h3>
                  <div className={styles.aiSummary}>
                    {selectedReport.aiSummary}
                  </div>
                </div>
              )}

              {/* Sent Info */}
              {selectedReport.sentAt && (
                <div className={styles.sentInfo}>
                  <Mail size={14} />
                  Sent via {selectedReport.sentVia} on {new Date(selectedReport.sentAt).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
